import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Plus, Plane } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import RaceTravelPlanDialog, { planSummary } from "@/components/RaceTravelPlanDialog";

/** Google Maps link for a race — admin-provided map_url or a search on the location. */
export function raceMapUrl(race) {
  return race.map_url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(race.location || "")}`;
}

/** Flag-off time if the race date carries one (admin uses datetime-local), else null. */
export function raceFlagOff(race) {
  if (!race.date || !race.date.includes("T")) return null;
  const d = new Date(race.date);
  return isNaN(d.getTime()) ? null : d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit" }).toUpperCase();
}

/** "ADD TRAVEL PLAN" / plan summary button — only for joined runners. */
export function RacePlanButton({ race, onChange, size = "md" }) {
  const { user } = useAuth();
  if (!user || !race.joined) return null;
  const pad = size === "sm" ? "px-3 py-2 text-xs" : "px-4 py-2.5 text-sm";
  const summary = planSummary(race.my_plan);
  return (
    <RaceTravelPlanDialog race={race} onChange={onChange}>
      <button
        type="button"
        data-testid={`race-plan-${race.race_id}`}
        title={summary || "Add your travel plan"}
        className={`inline-flex items-center gap-1.5 max-w-full font-display tracking-wider ${pad} transition border ${
          race.my_plan ? "border-zinc-700 text-zinc-200 hover:border-orange-600/60" : "border-dashed border-zinc-600 text-zinc-400 hover:text-white hover:border-zinc-400"
        }`}
      >
        <Plane size={14} className={race.my_plan ? "text-orange-500" : ""} />
        <span className="truncate">{race.my_plan ? `PLAN · ${summary.toUpperCase()}` : "ADD TRAVEL PLAN"}</span>
      </button>
    </RaceTravelPlanDialog>
  );
}

/** "Count me in" toggle for a race. Calls onChange(updatedRace) after the server responds. */
export function RaceJoinButton({ race, onChange, size = "md" }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const pad = size === "sm" ? "px-3 py-2 text-xs" : "px-4 py-2.5 text-sm";

  if (!user) {
    return (
      <Link
        to="/login"
        data-testid={`race-join-login-${race.race_id}`}
        className={`inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-300 hover:border-orange-600 hover:text-white font-display tracking-wider ${pad} transition`}
      >
        <Plus size={14} /> LOGIN TO JOIN
      </Link>
    );
  }

  const toggle = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/races/${race.race_id}/join`);
      onChange?.(r.data);
      toast.success(r.data.joined ? `You're in for ${race.title}!` : `Removed from ${race.title}`);
    } catch {
      toast.error("Could not update — try again");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`race-join-${race.race_id}`}
      aria-pressed={!!race.joined}
      className={`inline-flex items-center gap-1.5 font-display tracking-wider ${pad} transition disabled:opacity-60 ${
        race.joined
          ? "bg-orange-600 border border-orange-500 text-white hover:bg-orange-500"
          : "border border-orange-600/60 text-orange-500 hover:bg-orange-600 hover:text-white"
      }`}
    >
      {race.joined ? <Check size={14} /> : <Plus size={14} />}
      {race.joined ? "I'M IN" : "COUNT ME IN"}
    </button>
  );
}

/** Overlapping avatar stack + "N OSCR runners in" label. */
export function RaceJoiners({ race, max = 5 }) {
  const list = race.joiners || [];
  const count = race.joiners_count ?? list.length;
  if (count === 0) {
    return (
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-500" data-testid={`race-joiners-${race.race_id}`}>
        Be the first OSCR runner to join
      </p>
    );
  }
  const shown = list.slice(0, max);
  return (
    <div className="flex items-center gap-3" data-testid={`race-joiners-${race.race_id}`}>
      <div className="flex -space-x-2">
        {shown.map((u) => (
          <Avatar key={u.user_id} user={u} />
        ))}
        {count > shown.length && (
          <span className="w-7 h-7 rounded-full bg-zinc-800 border-2 border-zinc-950 flex items-center justify-center font-mono text-[9px] text-zinc-300">
            +{count - shown.length}
          </span>
        )}
      </div>
      <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-400">
        <span className="text-white">{count}</span> OSCR {count === 1 ? "runner" : "runners"} in
        {shown.length > 0 && <span className="text-zinc-600"> · {shown.map((u) => u.name.split(" ")[0]).join(", ")}</span>}
      </p>
    </div>
  );
}

function Avatar({ user }) {
  const initials = (user.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return user.picture ? (
    <img src={user.picture} alt={user.name} title={user.name} className="w-7 h-7 rounded-full object-cover border-2 border-zinc-950" />
  ) : (
    <span title={user.name} className="w-7 h-7 rounded-full bg-orange-600 border-2 border-zinc-950 flex items-center justify-center font-display text-[10px] text-white">
      {initials}
    </span>
  );
}
